import { getApiBaseUrl, getHeaders, handleApiError } from './apiClient';
import { request, requestWithAuth } from './request';

type DataDeletionRequest = {
  email: string;
  reason?: string;
};

// Only the fields any caller here reads. `GET /users/profile` returns the whole user document
// minus the password; narrowing it at the boundary keeps a points display from silently growing
// a dependency on, say, the email address.
export type MyProfile = {
  _id: string;
  full_name?: string;
  avatar_url?: string;
  points?: number;
};

/**
 * The authenticated user's own profile, read for its `points` balance.
 *
 * Why a round trip rather than `useAuth().user`: AuthContext exposes no refresh and its user
 * object does not carry `points`, so it can neither answer the question nor be brought up to
 * date after an award.
 *
 * Why the SERVER's number rather than the form's preview: the daily ceiling clamps, on a UTC day
 * boundary, so a user near the cap late in the Egyptian evening is credited less than the
 * preview promised. Showing the preview there would be a lie the ledger contradicts.
 */
export const fetchMyProfile = async (): Promise<MyProfile> =>
  requestWithAuth<MyProfile>({ path: '/users/profile', method: 'GET' });

// Mirrors what `userProfileService.getPublicProfile` returns. Fields the §8 visibility table
// withholds from the current viewer are ABSENT, not null — the server decides, and the presence
// of a key is the whole client-side rule.
export type PublicProfileReview = {
  _id: string;
  overallRating: number;
  text?: string;
  images?: string[];
  likesCount: number;
  createdAt: string;
  store?: { _id: string; name: string; slug?: string; logoUrl?: string };
};

export type PublicProfile = {
  id: string;
  full_name: string;
  avatar_url?: string;
  joinDate: string;
  tier: { level: number; ar: string; en: string; threshold: number };
  isVerified: boolean;
  points: number;
  counts: { reviews: number; photos: number; helpfulVotes: number };
  visibility: 'public' | 'private';
  viewer: { isOwner: boolean; isAdmin: boolean };
  progress?: { nextThreshold: number | null; pointsToNext: number | null };
  showNudge: boolean;
  reviews?: PublicProfileReview[];
};

export type PointsHistoryRow = {
  id: string;
  action: string;
  points: number;
  sourceType: 'review' | 'store' | 'account';
  sourceId?: string;
  reason?: string;
  createdAt: string;
};

/**
 * The profile behind /u/@id.
 *
 * Sent WITH the auth token when one exists (`requestWithAuth`) and without it otherwise, because
 * the single endpoint behind `optionalAuth` answers differently for a visitor, the owner and an
 * admin. Falling back to the anonymous request on a missing token is what keeps the page working
 * logged out rather than throwing `Authentication required` from the transport.
 */
export const fetchPublicProfile = async ({
  userId,
  before,
}: {
  userId: string;
  before?: string;
}): Promise<PublicProfile> => {
  const options = {
    path: `/users/${userId}/public`,
    method: 'GET' as const,
    params: before ? { before } : undefined,
  };
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  return token ? requestWithAuth<PublicProfile>(options) : request<PublicProfile>(options);
};

/** Owner and admins only — the server answers 403 to anyone else. */
export const fetchPointsHistory = async ({
  userId,
  before,
  limit,
}: {
  userId: string;
  before?: string;
  limit?: number;
}): Promise<PointsHistoryRow[]> =>
  requestWithAuth<PointsHistoryRow[]>({
    path: `/users/${userId}/points-history`,
    method: 'GET',
    params: { ...(before ? { before } : {}), ...(limit ? { limit } : {}) },
  });

export const requestDataDeletion = async ({ email, reason }: DataDeletionRequest): Promise<void> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/users/data-deletion`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ email, reason }),
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
  } catch (error) {
    console.error('Error requesting data deletion:', error);
    throw handleApiError(error);
  }
};
